import { useEffect, useState } from "react";

/**
 * usePreviewMode — founder/master-admin toggle to view the prelaunch and
 * beta-launch pages exactly as an anonymous visitor would.
 *
 * Phase 64 / Phase B (2026-05-13): when enabled, EditableText components
 * stop rendering pencil affordances and admin chrome (banners/preview
 * pill) hides. Survives a tab refresh via sessionStorage so the founder
 * can refresh-test the page without losing the preview state.
 *
 * Single source of truth lives in `_state` (module-level) so toggling from
 * the floating button updates every consumer in the tree without prop
 * drilling or a context provider.
 */
const STORAGE_KEY = "dhdh.previewAsVisitor";
const _state = {
  enabled: typeof window !== "undefined" && sessionStorage.getItem(STORAGE_KEY) === "1",
};
const _subscribers = new Set();

const broadcast = () => {
  _subscribers.forEach((fn) => {
    try { fn(_state.enabled); } catch { /* ignore */ }
  });
};

export const setPreviewAsVisitor = (next) => {
  _state.enabled = !!next;
  try {
    if (typeof window !== "undefined") {
      sessionStorage.setItem(STORAGE_KEY, _state.enabled ? "1" : "0");
    }
  } catch { /* ignore */ }
  broadcast();
};

export const usePreviewMode = () => {
  const [enabled, setEnabled] = useState(_state.enabled);
  useEffect(() => {
    _subscribers.add(setEnabled);
    return () => { _subscribers.delete(setEnabled); };
  }, []);
  return enabled;
};
